import { useTranslation } from 'react-i18next';
import { TextField } from '../TextField/Textfield';
import { Select } from '../Select/Select';
import classes from './SchedulingSettingsForm.module.css';

interface TimeInputGroupProps {
  label: string;
  hour: number;
  minute: number;
  onHourChange: (hour: number) => void;
  onMinuteChange: (minute: number) => void;
  inputType?: 'textfield' | 'select';
  disabled?: boolean;
}

const createTimeOptions = (max: number) =>
  Array.from({ length: max }, (_, i) => ({
    value: i.toString(),
    label: i.toString().padStart(2, '0'),
  }));

const HOUR_OPTIONS = createTimeOptions(24);
const MINUTE_OPTIONS = createTimeOptions(60);

export const TimeInputGroup = ({
  label,
  hour,
  minute,
  onHourChange,
  onMinuteChange,
  inputType = 'textfield',
  disabled = false,
}: TimeInputGroupProps) => {
  const { t } = useTranslation();

  if (inputType === 'textfield') {
    return (
      <div className={classes.timeGroup}>
        <label className={classes.timeGroupLabel}>{label}</label>
        <div className={classes.timeInputWrapper}>
          <input
            type="number"
            min={0}
            max={23}
            value={hour.toString().padStart(2, '0')}
            onChange={(e) => onHourChange(parseInt(e.target.value, 10) || 0)}
            className={classes.timeInputField}
            aria-label={t('scheduling.form.aria.hourInput', { label })}
            disabled={disabled}
            required
          />
          <span className={classes.timeSeparator}>:</span>
          <input
            type="number"
            min={0}
            max={59}
            value={minute.toString().padStart(2, '0')}
            onChange={(e) => onMinuteChange(parseInt(e.target.value, 10) || 0)}
            className={classes.timeInputField}
            aria-label={t('scheduling.form.aria.minuteInput', { label })}
            disabled={disabled}
            required
          />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.timeGroupSelect}>
      <label className={classes.label}>{label}</label>
      <div className={classes.timeSelectInputs}>
        <Select
          value={hour.toString()}
          options={HOUR_OPTIONS}
          onChange={(value) => onHourChange(parseInt(value, 10))}
          disabled={disabled}
        />
        <span className={classes.timeSeparator}>:</span>
        <Select
          value={minute.toString()}
          options={MINUTE_OPTIONS}
          onChange={(value) => onMinuteChange(parseInt(value, 10))}
          disabled={disabled}
        />
      </div>
    </div>
  );
};
